import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import debounce from "lodash.debounce";
import { BsImages, BsLayoutWtf } from "react-icons/bs";
import editorStore from "../../stores/EditorStore";
import BannerService, { BannerDetails } from "../../Services/BannerService";
import BannerHeader, { SaveStatus } from "./BannerHeader";
import BannerToolbar from "./BannerToolbar";
import BannerWorkspace from "./BannerWorkspace";
import BannerUploadPanel from "./BannerUploadPanel";
import BannerPreview from "./BannerPreview";
import { POINTS_PER_INCH } from "./bannerConfig";
import loadingSpinner from "../../assets/img/Spinner.svg";

type SidePanel = "board" | "photos";

const SIDE_TABS: { key: SidePanel; label: string; Icon: React.ComponentType<{ size?: number }> }[] = [
  { key: "board", label: "Board", Icon: BsLayoutWtf },
  { key: "photos", label: "Photos", Icon: BsImages },
];

/** Ids of the uploaded photos placed anywhere in the design. */
const collectUploadIds = (json: any): number[] => {
  const ids = new Set<number>();
  (json.pages || []).forEach((p: any) =>
    (p.fabricJSON?.objects || []).forEach((o: any) => { if (o.uploadId != null) ids.add(Number(o.uploadId)); })
  );
  return Array.from(ids);
};

const formatInches = (n: number) => String(Math.round(n * 100) / 100);

const BannerEditor = () => {
  const { bannerId } = useParams();
  const [details, setDetails] = useState<BannerDetails | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [sidePanel, setSidePanel] = useState<SidePanel>("board");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const hasUnsaved = useRef(false);

  // The workspace is mounted first, so the canvas exists by the time the banner arrives
  const loadBanner = useCallback(() => {
    const id = Number(bannerId);
    setLoadError(false);
    if (!Number.isInteger(id) || id <= 0) { setLoadError(true); return; }
    BannerService.GetBannerDetails(id)
      .then(d => {
        // Page = banner size plus the bleed on every side, in points
        const bleed = d.bleedIn * POINTS_PER_INCH;
        const safety = d.safetyIn * POINTS_PER_INCH;
        const width = (d.widthIn + 2 * d.bleedIn) * POINTS_PER_INCH;
        const height = (d.heightIn + 2 * d.bleedIn) * POINTS_PER_INCH;

        editorStore.clear();
        editorStore.setPrintGuides(bleed, safety);
        if (d.designJson) {
          // The banner's size is the source of truth for the page, not the size stored in the design
          editorStore.loadJSON({ ...d.designJson, width, height });
        } else {
          editorStore.setSize(width, height);
          editorStore.addPage();
        }
        setDetails(d);
        setLastSaved(d.lastUpdated ? new Date(d.lastUpdated) : null);
      })
      .catch(err => {
        console.error("Failed to load banner", err);
        setLoadError(true);
      });
  }, [bannerId]);

  useEffect(loadBanner, [loadBanner]);

  // Debounced auto-save, as in PhotobookEditor. Saves run one at a time so they land in order.
  useEffect(() => {
    if (!details) return;
    const saveBanner = () => {
      saveChain.current = saveChain.current.then(async () => {
        const json = editorStore.toJSON();
        // Don't overwrite the banner with an empty design once the editor has been torn down
        if (!json.pages || json.pages.length === 0) return;
        setSaveStatus("saving");
        try {
          await BannerService.UpdateBanner(details.id, json, collectUploadIds(json));
          hasUnsaved.current = false;
          setSaveStatus("saved");
          setLastSaved(new Date());
        } catch (err) {
          console.error("Failed to save banner", err);
          setSaveStatus("error");
        }
      });
    };
    const debouncedSave = debounce(saveBanner, 2000);
    const unsubscribe = editorStore.on("change", () => { hasUnsaved.current = true; debouncedSave(); });
    return () => { unsubscribe(); debouncedSave.flush(); };
  }, [details]);

  // Warn before closing the tab with a change that hasn't reached the server yet
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsaved.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#f5f5f5]">
      <BannerHeader
        projectName={details?.name ?? ""}
        sizeLabel={details ? `${formatInches(details.widthIn)} x ${formatInches(details.heightIn)}` : ""}
        date={details?.dateCreated ?? ""}
        saveStatus={saveStatus}
        lastSaved={lastSaved}
        onPreview={() => setIsPreviewOpen(true)}
      />
      <BannerToolbar />

      <div className="flex min-h-0 flex-1">
        <nav className="flex w-14 shrink-0 flex-col bg-[#e8e8e8] font-jakarta">
          {SIDE_TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setSidePanel(key)}
              className={`flex flex-col items-center gap-1 py-3 text-[10px] ${sidePanel === key ? "bg-white text-ink" : "text-gray-600 hover:bg-white/60"}`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>

        {sidePanel === "photos" && (
          <aside className="w-64 shrink-0 border-r border-gray-200 bg-white">
            <BannerUploadPanel />
          </aside>
        )}

        <div className="relative min-w-0 flex-1">
          <BannerWorkspace />
          {!details && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-[#f5f5f5] font-jakarta text-sm text-gray-600">
              {loadError ? (
                <>
                  We couldn't open this banner.
                  <button onClick={loadBanner} className="rounded bg-brand px-4 py-2 text-xs font-semibold text-white hover:bg-brand-dark">
                    Try again
                  </button>
                </>
              ) : (
                <img src={loadingSpinner} alt="Loading..." className="h-16 w-16" />
              )}
            </div>
          )}
        </div>
      </div>

      {isPreviewOpen && <BannerPreview onClose={() => setIsPreviewOpen(false)} />}
    </div>
  );
};

export default BannerEditor;
